import { Alert, Button, Card, Col, Input, Row, Space, Tag, Tooltip, Typography } from 'antd';
import {
  formatCreditEstimateSummary,
  formatCreditGrantText,
} from '../utils/creditEstimate';
import {
  formatPrice,
  resolveDurationLabel,
  resolveLifetimeMeta,
  resolvePlanPricingMeta,
} from '../utils/checkoutPricing';

const BillingPlanSection = ({
  error,
  orderedPlans,
  trialPromoEligibility,
  monthlyBasePriceCents,
  annualBasePriceCents,
  actionLoading,
  actionPlanId,
  onCheckout,
  redeemCode,
  onRedeemCodeChange,
  redeemLoading,
  onRedeem,
  campaignCode,
  onCampaignCodeChange,
  campaignPreviewMeta,
  campaignPreviewLoading,
  onPreviewCampaign,
  onResetCampaignPreview,
  formatStorage,
  quotaTooltipMap,
  trialPromoReasonTextMap,
}) => (
  <Card className="billing-card" title="订阅与兑换">
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {error && (
        <Alert
          type="error"
          showIcon
          message="加载失败"
          description={error}
        />
      )}
      <Row gutter={[12, 12]}>
        {(orderedPlans || []).map((plan) => {
          const pricing = resolvePlanPricingMeta(plan, monthlyBasePriceCents);
          const lifetime = resolveLifetimeMeta(plan, annualBasePriceCents);
          const campaignDiscountCents = Number(plan?.campaign_discount_cents || 0);
          const isCampaignAppliedPlan = campaignDiscountCents > 0;
          const isTrialPlan = String(plan?.plan_id || '').trim() === 'trial_3d';
          const publishTagLabel = isTrialPlan ? '总发布' : '月发布';
          const trialEligible = isTrialPlan
            ? (trialPromoEligibility?.eligible ?? plan?.promo_eligible ?? false)
            : null;
          const trialReason = isTrialPlan
            ? String(trialPromoEligibility?.reason || '').trim()
            : '';
          const trialHintText = isTrialPlan && trialEligible === true
            ? String(
              trialPromoEligibility?.reason_text
                || trialPromoReasonTextMap[trialReason]
                || trialPromoReasonTextMap.unused
            )
            : '';
          const creditGrantText = formatCreditGrantText(plan?.benefits);
          const creditGrantEstimateText = formatCreditEstimateSummary(plan?.benefits?.credit_grant_amount, { includePrefix: true });

          return (
            <Col xs={24} md={12} xl={12} key={plan.plan_id}>
              <Card className={`plan-card${isCampaignAppliedPlan ? ' plan-card--campaign-hit' : ''}`} bodyStyle={{ padding: 16 }}>
                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                  <div className="plan-card__title">
                    <Typography.Text strong>{plan.name || plan.plan_id}</Typography.Text>
                    <Space size={6}>
                      <Tag color="blue">{resolveDurationLabel(plan.duration_days)}</Tag>
                      {pricing.hasDiscount && pricing.discountLabel ? <Tag color="volcano">{pricing.discountLabel}</Tag> : null}
                      {isCampaignAppliedPlan ? <Tag color="green">渠道码优惠</Tag> : null}
                      {lifetime.isLifetime ? <Tag color="gold">{lifetime.tagLabel}</Tag> : null}
                    </Space>
                  </div>
                  <div className="plan-card__price">
                    <span className="plan-card__currency">¥</span>
                    <span className="plan-card__amount">{formatPrice(pricing.finalCents)}</span>
                    {pricing.hasDiscount ? (
                      <span className="plan-card__origin">¥{formatPrice(pricing.originalCents)}</span>
                    ) : null}
                  </div>
                  {isCampaignAppliedPlan ? (
                    <Typography.Text className="plan-card__saving">
                      渠道码立减 ¥{formatPrice(campaignDiscountCents)}
                    </Typography.Text>
                  ) : pricing.hasDiscount ? (
                    <Typography.Text className="plan-card__saving">
                      立省 ¥{formatPrice(pricing.savedCents)}
                    </Typography.Text>
                  ) : null}
                  {trialHintText ? (
                    <Typography.Text type="secondary">
                      {trialHintText}
                    </Typography.Text>
                  ) : null}
                  {lifetime.isLifetime ? (
                    <Typography.Text className="plan-card__lifetime">{lifetime.message}</Typography.Text>
                  ) : null}
                  {creditGrantText ? (
                    <Typography.Text type="secondary">
                      {creditGrantText}
                    </Typography.Text>
                  ) : null}
                  {creditGrantEstimateText ? (
                    <Typography.Text type="secondary" className="plan-card__estimate">
                      {creditGrantEstimateText}
                    </Typography.Text>
                  ) : null}
                  <div className="plan-card__limits">
                    <Tooltip title={quotaTooltipMap.accounts}>
                      <Tag style={{ cursor: 'help' }}>账号 {plan?.limits?.max_accounts ?? '-'}</Tag>
                    </Tooltip>
                    <Tooltip title={quotaTooltipMap.tasks}>
                      <Tag style={{ cursor: 'help' }}>任务 {plan?.limits?.max_tasks ?? '-'}</Tag>
                    </Tooltip>
                    <Tooltip title={quotaTooltipMap.monthly}>
                      <Tag style={{ cursor: 'help' }}>{publishTagLabel} {plan?.limits?.monthly_publish_limit ?? '-'}</Tag>
                    </Tooltip>
                    <Tooltip title={quotaTooltipMap.storage}>
                      <Tag style={{ cursor: 'help' }}>存储 {formatStorage(plan?.limits?.max_storage_bytes)}</Tag>
                    </Tooltip>
                  </div>
                  <Button
                    type="primary"
                    block
                    loading={actionLoading && actionPlanId === String(plan.plan_id || '')}
                    onClick={() => onCheckout(plan)}
                  >
                    扫码支付
                  </Button>
                </Space>
              </Card>
            </Col>
          );
        })}
      </Row>
      <div className="billing-section">
        <Typography.Text strong>优惠兑换</Typography.Text>
        <Typography.Text type="secondary">
          输入兑换码可直接开通对应会员权益，系统会自动叠加有效期。
        </Typography.Text>
        <div className="billing-redeem-row">
          <Input
            value={redeemCode}
            placeholder="请输入兑换码，例如：PUB-ABCD-EFGH-IJKL"
            autoComplete="off"
            maxLength={64}
            onChange={(e) => onRedeemCodeChange(e.target.value)}
            onPressEnter={() => {
              if (!redeemLoading) onRedeem();
            }}
          />
          <Button
            type="primary"
            className="billing-redeem-action-btn"
            loading={redeemLoading}
            onClick={onRedeem}
          >
            立即兑换
          </Button>
        </div>
        <Typography.Text strong>渠道码（折扣码）</Typography.Text>
        <Typography.Text type="secondary">
          下单时自动应用渠道优惠，并记录推广归因。
        </Typography.Text>
        <div className="billing-redeem-row">
          <Input
            value={campaignCode}
            placeholder="请输入渠道码（可选）"
            autoComplete="off"
            maxLength={64}
            onChange={(e) => onCampaignCodeChange(e.target.value)}
            onPressEnter={() => {
              if (!campaignPreviewLoading) onPreviewCampaign();
            }}
          />
          <Button
            type="primary"
            className="billing-redeem-action-btn"
            onClick={onPreviewCampaign}
            loading={campaignPreviewLoading}
          >
            折扣价
          </Button>
        </div>
        {campaignPreviewMeta?.applied ? (
          <Alert
            className="billing-campaign-alert"
            type="success"
            showIcon
            message={`渠道码已生效${campaignPreviewMeta.campaignName ? `：${campaignPreviewMeta.campaignName}` : ''}${campaignPreviewMeta.codeSuffix ? `（尾号 ${campaignPreviewMeta.codeSuffix}）` : ''}`}
            description={`命中 ${campaignPreviewMeta.appliedPlanCount} 个套餐${campaignPreviewMeta.maxDiscountCents > 0 ? `，最高优惠 ¥${formatPrice(campaignPreviewMeta.maxDiscountCents)}` : ''}`}
            action={(
              <Button
                size="small"
                onClick={onResetCampaignPreview}
                loading={campaignPreviewLoading}
              >
                清除
              </Button>
            )}
          />
        ) : null}
      </div>
    </Space>
  </Card>
);

export default BillingPlanSection;
